/**
 * `StepTransition` — one wizard step giving way to the next, in the direction
 * the user is travelling.
 *
 * Forward: the old step slips left, the new one arrives from the right.
 * Back: mirrored. The outgoing step finishes before the incoming starts
 * (`mode="wait"`), so the two never overlap in the same card, and the exit is
 * `instant` so the pause is felt as a beat, not a wait.
 *
 *   const [direction, setDirection] = useState<StepDirection>('forward');
 *   goNext = () => { setDirection('forward'); setStep(next); }
 *   <StepTransition stepKey={step} direction={direction}>…</StepTransition>
 */
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { step, StepDirection } from './presets';

interface StepTransitionProps {
  stepKey: string;
  direction: StepDirection;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** Animate the first step in on mount. Default off. */
  initial?: boolean;
}

export const StepTransition: React.FC<StepTransitionProps> = ({
  stepKey,
  direction,
  children,
  className,
  style,
  initial = false,
}) => (
  <AnimatePresence mode="wait" initial={initial} custom={direction}>
    <motion.div
      key={stepKey}
      className={className}
      style={style}
      custom={direction}
      variants={step}
      initial="enter"
      animate="center"
      exit="exit"
      data-step-transition={stepKey}
      data-step-direction={direction}
    >
      {children}
    </motion.div>
  </AnimatePresence>
);

export type { StepDirection };
